import { Component, Host, h, Element, Listen, Prop, Watch } from '@stencil/core';
import { Flavor } from './types';

type Size = 'mini' | 'small' | 'medium';

export interface AhaRadioButtonGroup extends HTMLAhaRadioButtonGroupElement {}

/**
 * A grouping element which contains any number of `<aha-radio-button>` elements, and which ensures that only a single button within each group of button elements is selected.
 *
 * @slot default - Contents of the default slot. Typically will be `<aha-radio-button>` elements.
 */
@Component({
  tag: 'aha-radio-button-group',
  styleUrl: 'aha-radio-button-group.css',
  shadow: true,
})
export class AhaRadioButtonGroup {
  @Element() host!: HTMLAhaRadioButtonGroupElement;

  /** The type or "flavor" of an `<aha-radio-button-group>`. Gives pre-determined stylistic options. */
  @Prop({ reflect: true }) flavor?: Flavor;

  /** When set, gives the `<aha-radio-button-group>` pre-determined stylistic size options. */
  @Prop({ reflect: true }) size?: Size;

  /** Optional. The name of any unnamed `<aha-radio-button>` elements. When set, any `<aha-radio-button>` with a value will be sent to the server with this name when embedded inside a `<form>` tag. The `name` attribute can also be set on individual `<aha-radio-button>` elements. */
  @Prop({ reflect: true }) name?: string;
  @Watch('name')
  private updateUnnamedRadioButtons(value: string | undefined, oldValue: string | undefined) {
    this.childButtons.forEach(button => {
      if (!button.name || button.name === oldValue) button.name = value;
    });
  }

  /** If the component is in it's first render. READ ONLY. */
  // We do this because we do NOT want the button animation on the first load, and the component may not be considered fully loaded until after it is mounted, rendered, and resized depending on the number of child elements in it.
  @Prop({ reflect: true, attribute: 'first-render' }) firstRender: boolean = true;

  // Unselect every other <aha-radio-button> one is clicked.
  @Listen('aha-radio-button:change')
  private handleRadioButtonChange(event: CustomEvent) {
    const changedButton = event.detail as HTMLAhaRadioButtonElement;
    this.childButtons.forEach(elem => {
      elem.selected = elem === changedButton ? true : false;
    });

    this.highlightSelectedButton();
  }

  private highlightSelectedButton() {
    if (this.pseudoButton) {
      // We cannot do a `this.host.querySelector('aha-radio-button[selected])` here as it is possible that each button's `selected` attribute does not yet match its internal `selected` prop, due to the way that Stencil syncs attributes to prop state only on the next rendering tick. A subtle yet infuriating bug.
      const elem = this.childButtons.find(elem => elem.selected);
      if (elem) {
        // Animate the button being selected.
        const { width, height } = elem.getBoundingClientRect();
        const left = elem.offsetLeft;
        const top = elem.offsetTop;
        Object.assign(this.pseudoButton.style, {
          top: `${top}px`,
          left: `${left}px`,
          width: `${width}px`,
          height: `${height}px`,
        });
      }
    }
  }

  connectedCallback() {
    this.handleSlotChange = this.handleSlotChange.bind(this);
  }

  componentDidLoad() {
    this.highlightSelectedButton();

    // Observe the size of the host, if it changes, it's likely that the pseudo-button needs to be moved.
    new ResizeObserver(() => {
      this.highlightSelectedButton();
    }).observe(this.host);

    // Add a delay to hopefully ensure that the component is fully rendered before the animation takes place.
    setTimeout(() => {
      this.firstRender = false;
    }, 250);
  }

  get childButtons() {
    return Array.from(this.host.querySelectorAll('aha-radio-button'));
  }

  // Select the first button if no buttons within the `<aha-button-group>` are selected.
  private handleSlotChange() {
    const buttons = this.childButtons;
    if (buttons.length) {
      // Make any unnamed `<aha-radio-button>` inherit the name of the `<aha-radio-group>` if it is defined.
      if (this.name) {
        buttons.forEach(button => {
          if (!button.name) button.name = this.name;
        });
      }

      // Select the first button if none is already selected.
      let selectedButton = buttons.find(
        button => button.selected || button.hasAttribute('selected'),
      );
      if (!selectedButton) {
        selectedButton = buttons[0] as HTMLAhaRadioButtonElement;
        selectedButton.selected = true;
      }
      // Ensure that the selected button is highlighted.
      this.highlightSelectedButton();
    }
  }

  // The background that animates, showing which button is selected.
  private pseudoButton?: HTMLDivElement;

  render() {
    return (
      <Host>
        <div style={{ position: 'relative' }}>
          <div ref={el => (this.pseudoButton = el)} id="pseudo-button"></div>
          <div id="content">
            <slot onSlotchange={this.handleSlotChange}></slot>
          </div>
        </div>
      </Host>
    );
  }
}
